/**
 * 文件组组件
 *
 * 显示为可折叠的文件组卡片，内含成员文件列表。
 * 支持组名编辑、拆散操作、将文件拖入/拖出。
 */
import React, { useState } from "react";
import { useDroppable } from "@dnd-kit/core";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, Input, Tag, Tooltip, Space, Typography } from "antd";
import {
    FolderOutlined,
    FolderOpenOutlined,
    EditOutlined,
    DisconnectOutlined,
    CheckOutlined,
    CloseOutlined,
    DownOutlined,
    RightOutlined,
    DragOutlined,
} from "@ant-design/icons";
import FileItem from "./FileItem";

const { Text } = Typography;

const FileGroupItem = ({
    group,
    memberFiles,
    onRename,
    onDisband,
    onRemoveFile,
    onRemoveFromGroup,
    onSplitFile,
}) => {
    const [expanded, setExpanded] = useState(true);
    const [editing, setEditing] = useState(false);
    const [editName, setEditName] = useState(group.name || "");

    // 让组自身作为排序元素
    const {
        attributes,
        listeners,
        setNodeRef: setSortableRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: group.id });

    // 让组成为 drop target（文件可以拖入）
    const { setNodeRef: setDroppableRef, isOver } = useDroppable({
        id: `group-drop-${group.id}`,
        data: { type: "group", groupId: group.id },
    });

    const sortableStyle = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    const handleSaveName = () => {
        onRename?.(group.id, editName.trim() || null);
        setEditing(false);
    };

    const handleCancelEdit = () => {
        setEditName(group.name || "");
        setEditing(false);
    };

    const uploadedCount = memberFiles.filter((f) => f.phase === "UPLOADED").length;
    const displayName = group.name || "未命名组";

    return (
        <div
            ref={(node) => {
                setSortableRef(node);
                setDroppableRef(node);
            }}
            style={sortableStyle}
            className={`upload-file-group ${isOver ? "upload-file-group--drop-over" : ""} ${isDragging ? "upload-file-group--dragging" : ""}`}
        >
            {/* 组头 */}
            <div className="upload-file-group__header">
                <div className="upload-file-group__drag" {...attributes} {...listeners}>
                    <DragOutlined style={{ color: "#bfbfbf", cursor: "grab" }} />
                </div>

                <Button
                    type="text"
                    size="small"
                    icon={expanded ? <DownOutlined style={{ fontSize: 10 }} /> : <RightOutlined style={{ fontSize: 10 }} />}
                    onClick={() => setExpanded(!expanded)}
                    style={{ padding: "0 4px" }}
                />

                <span className="upload-file-group__icon">
                    {expanded ? <FolderOpenOutlined style={{ color: "#fa8c16", fontSize: 18 }} /> : <FolderOutlined style={{ color: "#fa8c16", fontSize: 18 }} />}
                </span>

                <div className="upload-file-group__name">
                    {editing ? (
                        <Space size={4}>
                            <Input
                                size="small"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onPressEnter={handleSaveName}
                                style={{ width: 160 }}
                                maxLength={200}
                                autoFocus
                            />
                            <Button type="text" size="small" icon={<CheckOutlined />} onClick={handleSaveName} />
                            <Button type="text" size="small" icon={<CloseOutlined />} onClick={handleCancelEdit} />
                        </Space>
                    ) : (
                        <Space size={4} align="center">
                            <Text strong style={{ fontSize: 13 }}>{displayName}</Text>
                            <Tag color="orange" style={{ margin: 0 }}>
                                {memberFiles.length}文件
                            </Tag>
                            {uploadedCount < memberFiles.length && (
                                <Tag color="processing" style={{ margin: 0 }}>
                                    {uploadedCount}/{memberFiles.length}就绪
                                </Tag>
                            )}
                        </Space>
                    )}
                </div>

                <div className="upload-file-group__actions">
                    <Space size={4}>
                        {!editing && (
                            <Tooltip title="修改组名">
                                <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() => {
                                        setEditName(group.name || "");
                                        setEditing(true);
                                    }}
                                />
                            </Tooltip>
                        )}
                        <Tooltip title="拆散文件组">
                            <Button
                                type="text"
                                size="small"
                                danger
                                icon={<DisconnectOutlined />}
                                onClick={() => onDisband?.(group.id)}
                            />
                        </Tooltip>
                    </Space>
                </div>
            </div>

            {/* 组内文件列表 */}
            {expanded && (
                <div className="upload-file-group__body">
                    {memberFiles.map((file) => (
                        <FileItem
                            key={file.id}
                            file={file}
                            isGroupMember={true}
                            onRemove={onRemoveFile}
                            onSplit={onSplitFile}
                            onRemoveFromGroup={onRemoveFromGroup}
                        />
                    ))}
                    {memberFiles.length === 0 && (
                        <div className="upload-file-group__empty">
                            <Text type="secondary">拖入文件以添加到此组</Text>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default FileGroupItem;
